/**
 * Collection Filters System - Moon Moon Agency v2.1
 * Shopify collection filtering with AJAX updates, custom events, and UI sync
 */

// Initialisation de l'objet queryParams pour stocker les paramètres de requête
Shopify.queryParams = {};

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const COLLECTION_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,
	
	// UI Settings
	mobileBreakpoint: 1024,
	defaultOpenFilters: 3,
	
	// Price Slider Settings
	priceGap: 10,
	
	// Selectors - Using data attributes for better separation of concerns
	selectors: {
		form: '[data-collection-form]',
		grid: '[data-collection-grid]',
		gridCollection: '[data-grid-collection]',
		shopifySection: '.shopify-section', // Keep as class (Shopify generated)
		activeFilters: '[data-active-filters]',
		reloadElements: '[data-collection-reload]',
		sortBy: '[data-sort-by]',
		sortByHidden: '[data-sort-by-hidden]',
		
		// Mobile filters
		mobileFilterWrapper: '[data-mobile-filters]',
		htmlElement: 'html',
		
		// Filter accordions
		filterHeaders: '[data-filter-header]',
		
		// Price sliders
		minRange: '[data-price-min]',
		maxRange: '[data-price-max]',
		sliderTrack: '[data-price-track]',
		minRangeValue: '[data-price-min-value]',
		maxRangeValue: '[data-price-max-value]',
		priceMinValue: '[data-price-min-display]',
		priceMaxValue: '[data-price-max-display]'
	},

	// Development mode (lazy evaluation)
	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const collectionLog = (level, message, data = {}) => {
	if (!COLLECTION_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`🏷️ [COLLECTION] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Use global debounce from mm_utils.js
const collectionDebounce = window.debounce || ((fn, delay) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn.apply(this, args), delay);
	};
});

// Collection Event Emitter
const collectionEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'collection'
	};
	
	return document.dispatchEvent(new CustomEvent(`collection:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const CollectionDOM = {
	get form() { return document.querySelector(COLLECTION_CONFIG.selectors.form); },
	get grid() { return document.querySelector(COLLECTION_CONFIG.selectors.grid); },
	get gridCollection() { return document.querySelector(COLLECTION_CONFIG.selectors.gridCollection); },
	get sortBySelector() { return document.querySelector(COLLECTION_CONFIG.selectors.sortBy); },
	get sortByHidden() { return document.querySelector(COLLECTION_CONFIG.selectors.sortByHidden); },
	get html() { return document.querySelector(COLLECTION_CONFIG.selectors.htmlElement); },
	get mobileFilterWrapper() { return document.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper); },
	
	get activeFilters() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters); },
	get reloadElements() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements); },
	get filterHeaders() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.filterHeaders); },
	get formInputs() { return this.form?.querySelectorAll('input') || []; },
	
	// Price sliders
	get priceSliders() {
		return {
			minRange: document.querySelector(COLLECTION_CONFIG.selectors.minRange),
			maxRange: document.querySelector(COLLECTION_CONFIG.selectors.maxRange),
			sliderTrack: document.querySelector(COLLECTION_CONFIG.selectors.sliderTrack),
			minRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.minRangeValue),
			maxRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.maxRangeValue)
		};
	},
	
	get shopifySection() {
		const gridElem = this.gridCollection;
		return gridElem?.closest(COLLECTION_CONFIG.selectors.shopifySection);
	}
};

/*********************************************************************************************
 * 🔌 CORE COLLECTION API
 */
const CollectionAPI = {
	async request(url) {
		collectionEmit('api-start', { url });
		
		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			
			// Check if response is JSON (Section Rendering API) or HTML (fallback)
			const contentType = response.headers.get('content-type');
			const data = contentType && contentType.includes('application/json') 
				? await response.json() 
				: await response.text();
			
			collectionEmit('api-success', { url, responseType: typeof data });
			return data;
		} catch (error) {
			collectionEmit('api-error', { url, error: error.message });
			throw error;
		}
	},

	getSectionId() {
		const section = CollectionDOM.shopifySection;
		if (!section) {
			throw new Error('Collection grid element or shopify section not found');
		}
		return section.id;
	},

	getSectionIdShort() {
		return this.getSectionId().replace('shopify-section-', '');
	},

	async refreshWithFilters(url_filters) {
		collectionEmit('filter-start', { filters: url_filters });
		
		try {
			const currentUrl = `${window.location.pathname}`;
			const sectionId = this.getSectionIdShort();
			
			// Use Shopify Section Rendering API
			const requestUrl = `${currentUrl}?${url_filters}&sections=${sectionId}`;
			const response = await this.request(requestUrl);
			
			if (!CollectionDOM.grid) {
				throw new Error('Collection grid element not found');
			}

			// Handle different response types
			let newSectionHtml;
			if (typeof response === 'object' && response[sectionId]) {
				// Section Rendering API JSON response
				newSectionHtml = response[sectionId];
			} else if (typeof response === 'string') {
				// Fallback: HTML response
				const newPageEl = document.createElement('DIV');
				newPageEl.innerHTML = response;
				const newSection = newPageEl.querySelector(COLLECTION_CONFIG.selectors.grid);
				if (!newSection) {
					throw new Error('Required collection elements not found in response');
				}
				newSectionHtml = newSection.outerHTML;
			} else {
				throw new Error('Invalid response format');
			}

			this.updateDOM(newSectionHtml, currentUrl, url_filters);
			collectionEmit('filter-success', { filters: url_filters, url: `${currentUrl}?${url_filters}` });
			
		} catch (error) {
			collectionEmit('filter-error', { error: error.message, filters: url_filters });
			
			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-collection-loading');
			}
			
			// Fallback: reload page if AJAX fails
			window.location.search = url_filters;
		}
	},

	updateDOM(newSectionHtml, currentUrl, url_filters) {
		const tempEl = document.createElement('DIV');
		tempEl.innerHTML = newSectionHtml;

		const wasFiltersOpen = CollectionDOM.mobileFilterWrapper?.classList.contains('mm-open');
		
		// 1. Capture current states with high precision
		const openAccordions = Array.from(document.querySelectorAll('[data-filter-header].mm-active'))
                                    .map(h => ({
                                        id: h.closest('[data-filter-id]')?.dataset.filterId,
                                        height: h.nextElementSibling?.style.maxHeight
                                    }))
                                    .filter(item => item.id);
        const openIds = openAccordions.map(a => a.id);

		// 2. Prepare new content in shadow DOM to match current state
        // This avoids the "flash of closed menus" since they will be injected already OPEN
		const newMobileFilters = tempEl.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper);
		if (newMobileFilters) {
            newMobileFilters.querySelectorAll('[data-filter-id]').forEach(newWrapper => {
                const id = newWrapper.dataset.filterId;
                const match = openAccordions.find(a => a.id === id);
                if (match) {
                    const header = newWrapper.querySelector('[data-filter-header]');
                    const content = newWrapper.querySelector('.mm-filter-tag-form');
                    if (header) header.classList.add('mm-active');
                    if (content) {
                        content.style.visibility = 'visible';
                        content.style.transition = 'none'; // Disable transition for injection
                        content.style.maxHeight = match.height || '1000px'; 
                    }
                }
            });
        }

		// 3. Update products grid
		const newGridCollection = tempEl.querySelector(COLLECTION_CONFIG.selectors.grid);
		if (newGridCollection && CollectionDOM.grid) {
			CollectionDOM.grid.innerHTML = newGridCollection.innerHTML;
		}

		// 3b. Update other reloadable elements (header, sticky cta, etc)
		const currentReloadElements = CollectionDOM.reloadElements;
		if (currentReloadElements.length > 0) {
			currentReloadElements.forEach((el, index) => {
				// We can't really match by index safely if the structure changed, 
				// but for these specific snippets it should be fine.
				// Better: try to find a matching element in tempEl using classes or better selectors if possible.
				// For now, let's assume they are stable or use their own "identifier" if added.
				
				// Let's try to find a match by a unique combination of classes if data-collection-reload doesn't have a value
				const classes = Array.from(el.classList).join('.');
				const match = tempEl.querySelector(`.${classes}[data-collection-reload]`) || tempEl.querySelectorAll('[data-collection-reload]')[index];
				
				if (match) {
					// Preserve certain state classes that might be managed by JS
					const isVisible = el.classList.contains('is-visible');
					const isOpen = el.classList.contains('mm-open');
					
					el.innerHTML = match.innerHTML;
					
					// Update classes but restore preserved states
					if (match.hasAttribute('class')) {
						el.className = match.className;
						if (isVisible) el.classList.add('is-visible');
						if (isOpen) el.classList.add('mm-open');
					}
				}
			});
		}

		// 4. Update filters with prepared content
		if (newMobileFilters && CollectionDOM.mobileFilterWrapper) {
			CollectionDOM.mobileFilterWrapper.innerHTML = newMobileFilters.innerHTML;
			if (wasFiltersOpen) {
				CollectionDOM.mobileFilterWrapper.classList.add('mm-open');
			}
		}

		// 5. Update browser state
		const newUrl = `${currentUrl}?${url_filters}`;
		window.history.replaceState(null, document.title, newUrl);

		if (CollectionDOM.grid) {
			CollectionDOM.grid.classList.remove('mm-collection-loading');
		}

		// 6. Re-init components
		CollectionFilters.init();
		FilterAccordions.init(openIds, true); // Still run init to fix heights and add listeners
		PriceSliders.init();
		Pagination.init();
		SortBy.init();
		if (typeof StickyFilterCTA !== 'undefined') StickyFilterCTA.init();
		
		if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms();
		if (typeof window.initButtonCharacterStagger === 'function') window.initButtonCharacterStagger();
	}
};

/*********************************************************************************************
 * 🔍 COLLECTION FILTERS MANAGEMENT
 */
const CollectionFilters = {
	init() {
		if (!CollectionDOM.form) {
			collectionEmit('form-error', { error: 'Collection facets form not found' });
			return;
		}
		
		const inputs = CollectionDOM.formInputs;
		
		// Add event listeners to inputs
		inputs.forEach(input => {
			// Remove existing listeners to avoid duplicates
			input.removeEventListener('change', this.handleInputChange);
			input.addEventListener('change', this.handleInputChange);

            // Accessiblity: Handle "Enter" key for labels/inputs
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    if (input.type === 'checkbox') {
                        input.checked = !input.checked;
                        // Trigger change manually
                        input.dispatchEvent(new Event('change'));
                    } else if (input.type === 'radio') {
                        if (!input.checked) {
                            input.checked = true;
                            input.dispatchEvent(new Event('change'));
                        }
                    } else if (input.type === 'number' || input.type === 'range') {
                        // For numbers, enter should just trigger the submit via the change event
                        input.dispatchEvent(new Event('change'));
                    }
                }
            });
		});
		
		collectionEmit('filters-initialized', { inputCount: inputs.length });
	},

	handleInputChange(event) {
		collectionEmit('input-change', { 
			name: event.target.name || event.target.id,
			value: event.target.value,
			checked: event.target.checked 
		});
		
		CollectionFilters.submitForm();
	},

	async submitForm() {
		if (!CollectionDOM.form || !CollectionDOM.grid) {
			collectionEmit('form-error', { error: 'Required elements not found' });
			return;
		}
		
		try {
			collectionEmit('form-submit-start');
			
			const formData = new FormData(CollectionDOM.form);
			const url_filters = new URLSearchParams(formData).toString();
			
			CollectionDOM.grid.classList.add('mm-collection-loading');
			
			await CollectionAPI.refreshWithFilters(url_filters);
			
			collectionEmit('form-submit-success', { filters: url_filters });
			
		} catch (error) {
			collectionEmit('form-submit-error', { error: error.message });
			
			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-collection-loading');
			}
		}
	}
};

/*********************************************************************************************
 * 📱 MOBILE FILTERS
 */
const MobileFilters = {
	isMobile() {
		return window.innerWidth < COLLECTION_CONFIG.mobileBreakpoint;
	},

    lastFocusedElement: null,

	open() {
		if (!this.isMobile()) return;
		
		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const html = CollectionDOM.html;
		
		if (wrapperMobile && html) {
            this.lastFocusedElement = document.activeElement;
			wrapperMobile.classList.add('mm-open');
			html.classList.add('stop-scrolling');
			collectionEmit('mobile-filters-opened');

            // Focus first focusable element in drawer after a short delay for animation
            setTimeout(() => {
                const focusableElements = wrapperMobile.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                if (focusableElements.length) focusableElements[0].focus();
            }, 400);
		}
	},

	close() {
		if (!this.isMobile()) return;
		
		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const html = CollectionDOM.html;
		
		if (wrapperMobile && html) {
			wrapperMobile.classList.remove('mm-open');
			html.classList.remove('stop-scrolling');
			collectionEmit('mobile-filters-closed');

            if (this.lastFocusedElement) {
                this.lastFocusedElement.focus();
            }
		}
	},

	checkDrawer() {
		if (!this.isMobile()) return;
		
		const hasOpenFilters = document.location.href.includes('open_filters');
		if (hasOpenFilters) {
			this.open();
		}
	},

    handleKeyDown(e) {
        if (!this.isMobile()) return;
        const wrapperMobile = CollectionDOM.mobileFilterWrapper;
        if (!wrapperMobile?.classList.contains('mm-open')) return;

        if (e.key === 'Escape') {
            this.close();
            return;
        }

        // Entrée/Espace : coche/décoche l'option mobile focusée
        if ((e.key === 'Enter' || e.key === ' ') && e.target.classList.contains('mm-filter-mobile-option')) {
            e.preventDefault();
            const cb = e.target.querySelector('input[type="checkbox"]');
            if (cb && !cb.disabled) {
                cb.checked = !cb.checked;
                cb.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
        }

        if (e.key === 'Tab') {
            const focusableElements = Array.from(wrapperMobile.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
                .filter(el => {
                    const style = window.getComputedStyle(el);
                    return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
                });
            
            if (focusableElements.length === 0) return;

            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    last.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === last) {
                    first.focus();
                    e.preventDefault();
                }
            }
        }
    },

	init() {
		if (this.initialized) return;

		// Délégation globale sur le document pour tous les boutons de filtres mobiles
		document.addEventListener('click', (e) => {
			// Bouton Ouvrir
			const openBtn = e.target.closest('[data-open-filters-mobile]');
			if (openBtn) {
				if (typeof window.mmCloseMobileMenu === 'function') window.mmCloseMobileMenu();
				this.open();
				return;
			}

			// Bouton Fermer
			const closeBtn = e.target.closest('[data-filter-close]');
			if (closeBtn) {
				this.close();
				return;
			}

			// Bouton "Appliquer" (submit)
			const submitBtn = e.target.closest('[data-filter-submit]');
			if (submitBtn) {
				if (typeof CollectionFilters.submitForm === 'function') {
					CollectionFilters.submitForm();
				}
				return;
			}

			// Backdrop Click
			if (e.target.matches('[data-mobile-filters]')) {
				this.close();
			}
		});

        document.addEventListener('keydown', (e) => this.handleKeyDown(e));

		this.initialized = true;
	}
};

/*********************************************************************************************
 * 🎛️ FILTER ACCORDIONS
 */
const FilterAccordions = {
	init(savedOpenIds = null, skipAnimation = false) {
		const filtersHeaders = CollectionDOM.filterHeaders;

		filtersHeaders.forEach((filtersHeader, index) => {
			const wrapper = filtersHeader.closest('[data-filter-id]');
			const content = filtersHeader.nextElementSibling;
			
            const isSavedOpen = savedOpenIds !== null && wrapper && savedOpenIds.includes(wrapper.dataset.filterId);
            const shouldBeOpen = savedOpenIds === null ? filtersHeader.classList.contains('mm-active') : isSavedOpen;

			filtersHeader.setAttribute('aria-expanded', shouldBeOpen ? 'true' : 'false');

			if (shouldBeOpen) {
				filtersHeader.classList.add('mm-active');
				if (content) {
                    content.style.visibility = 'visible';
                    if (skipAnimation) {
                        content.style.transition = 'none';
                        content.style.maxHeight = (content.scrollHeight + 1) + 'px';
                        // Force reflow to apply the transition:none and maxHeight immediately
                        content.offsetHeight;
                        // Restoration of transitions in the next frame
                        requestAnimationFrame(() => {
                            content.style.transition = '';
                        });
                    } else {
                        // Standard animation with a small delay for DOM readiness
                        setTimeout(() => {
                            content.style.maxHeight = (content.scrollHeight + 1) + 'px';
                        }, 10);
                    }
				}
			} else {
				filtersHeader.classList.remove('mm-active');
				if (content) {
                    if (skipAnimation) {
                        content.style.transition = 'none';
                        content.style.maxHeight = '0px';
                        content.style.visibility = 'hidden';
                        content.offsetHeight;
                        requestAnimationFrame(() => {
                            content.style.transition = '';
                        });
                    } else {
                        content.style.maxHeight = '0px';
                        // Wait for transition to finish before hiding visibility
                        setTimeout(() => {
                            if (!filtersHeader.classList.contains('mm-active')) {
                                content.style.visibility = 'hidden';
                            }
                        }, 300);
                    }
                }
			}
            
			filtersHeader.addEventListener('click', () => {
				filtersHeader.classList.toggle('mm-active');
				const isOpen = filtersHeader.classList.contains('mm-active');
				filtersHeader.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
				const targetContent = filtersHeader.nextElementSibling;

				if (targetContent) {
                    if (isOpen) {
                        targetContent.style.visibility = 'visible';
                        targetContent.style.maxHeight = (targetContent.scrollHeight + 1) + 'px';
                    } else {
                        targetContent.style.maxHeight = '0px';
                        setTimeout(() => {
                            if (!filtersHeader.classList.contains('mm-active')) {
                                targetContent.style.visibility = 'hidden';
                            }
                        }, 300);
                    }
				}

				collectionEmit('filter-accordion-toggle', { index, active: isOpen });
			});

			filtersHeader.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					filtersHeader.click();
				}
			});
		});
		
		collectionEmit('accordions-initialized', { count: filtersHeaders.length });
	}
};

/*********************************************************************************************
 * 💰 PRICE SLIDERS
 */
const PriceSliders = {
	get elements() {
		return CollectionDOM.priceSliders;
	},
	
	isReady() {
		const elements = this.elements;
		return elements.minRange && elements.maxRange && 
				elements.sliderTrack && elements.minRangeValue && 
				elements.maxRangeValue;
	},

	init() {
		if (!this.isReady()) return;
		
		const { minRange, maxRange, minRangeValue, maxRangeValue } = this.elements;
		
		const minValueEl = minRangeValue?.querySelector(COLLECTION_CONFIG.selectors.priceMinValue);
		const maxValueEl = maxRangeValue?.querySelector(COLLECTION_CONFIG.selectors.priceMaxValue);

		// Thumbs listeners
		minRange.addEventListener('input', () => {
			if (parseInt(minRange.value) > parseInt(maxRange.value) - COLLECTION_CONFIG.priceGap) {
				minRange.value = maxRange.value - COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});

		maxRange.addEventListener('input', () => {
			if (parseInt(maxRange.value) < parseInt(minRange.value) + COLLECTION_CONFIG.priceGap) {
				maxRange.value = parseInt(minRange.value) + COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});

		// Number inputs listeners (Sync display -> slider)
		if (minValueEl) {
			minValueEl.addEventListener('input', () => {
				minRange.value = minValueEl.value;
				this.updateRange();
			});
		}
		if (maxValueEl) {
			maxValueEl.addEventListener('input', () => {
				maxRange.value = maxValueEl.value;
				this.updateRange();
			});
		}
		
		// Initial update
		this.updateRange();
		collectionEmit('price-sliders-initialized');
	},

	updateRange() {
		if (!this.isReady()) {
			collectionEmit('price-error', { error: 'Price slider elements not found' });
			return;
		}
		
		const { minRange, maxRange, sliderTrack, minRangeValue, maxRangeValue } = this.elements;
		
		const minValue = parseInt(minRange.value, 10); 
		const maxValue = parseInt(maxRange.value, 10);

		// Update min and max values display
		const minValueEl = minRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMinValue);
		const maxValueEl = maxRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMaxValue);
		
		// Update values (handle both inputs and simple elements)
		if (minValueEl) {
			if (minValueEl.tagName === 'INPUT') minValueEl.value = minValue;
			else minValueEl.innerHTML = minValue;
		}
		if (maxValueEl) {
			if (maxValueEl.tagName === 'INPUT') maxValueEl.value = maxValue;
			else maxValueEl.innerHTML = maxValue;
		}
		
		// Calculate percentages for track background
		const minPercent = (minValue / minRange.max) * 100;
		const maxPercent = (maxValue / maxRange.max) * 100;

		// Update track background to reflect selected price range
		// Using theme variables for consistency: black-100 for background and black-900 for active range
		sliderTrack.style.background = `linear-gradient(to right, var(--black-100) ${minPercent}%, var(--black-900) ${minPercent}%, var(--black-900) ${maxPercent}%, var(--black-100) ${maxPercent}%)`;
		
		collectionEmit('price-update', { minValue, maxValue, minPercent, maxPercent });
	}
};

/*********************************************************************************************
 * 🔄 SORT BY FUNCTIONALITY
 */
const SortBy = {
	init() {
		const sortBySelectors = document.querySelectorAll(COLLECTION_CONFIG.selectors.sortBy);
		if (sortBySelectors.length === 0) return;
		
		sortBySelectors.forEach(selector => {
			selector.removeEventListener('change', this.handleChange);
			selector.addEventListener('change', this.handleChange);
		});
		
		collectionEmit('sort-initialized', { count: sortBySelectors.length });
	},

	handleChange(e) {
		const value = e.target.value;
		collectionEmit('sort-change', { value, previousValue: Shopify.queryParams.sort_by });
		
		// Update global query params
		Shopify.queryParams.sort_by = value;
		
		// Sync all other sort-by selects on the page
		document.querySelectorAll('select[name="sort_by"]').forEach(select => {
			if (select.value !== value) {
				select.value = value;
				// If it's a custom dropdown sibling, we might need to update its UI
				const wrapper = select.closest('.mm-sort-by')?.querySelector('.mm-sort-by-custom-wrapper');
				if (wrapper) {
					const option = wrapper.querySelector(`.mm-sort-by-option[data-value="${value}"]`);
					if (option) {
						wrapper.querySelector('.mm-sort-by-current-name').textContent = option.dataset.name;
						wrapper.querySelectorAll('.mm-sort-by-option').forEach(opt => opt.classList.remove('is-selected'));
						option.classList.add('is-selected');
					}
				}
			}
		});

		// Sync the hidden form field used for AJAX requests
		if (CollectionDOM.sortByHidden) {
			CollectionDOM.sortByHidden.value = value;
		}

		// Submit form for AJAX update
		CollectionFilters.submitForm();
	}
};

/*********************************************************************************************
 * 📄 PAGINATION
 */
const Pagination = {
	init() {
		const grid = CollectionDOM.grid;
		if (!grid) return;

		const paginationLinks = grid.querySelectorAll('.mm-paginate-link');
		paginationLinks.forEach(link => {
			link.removeEventListener('click', this.handlePaginationClick);
			link.addEventListener('click', this.handlePaginationClick);
		});
	},

	handlePaginationClick(e) {
		e.preventDefault();
		const link = e.currentTarget;
		const url = new URL(link.href);
		const searchParams = url.searchParams.toString();
		
		CollectionAPI.refreshWithFilters(searchParams);
		
		// Scroll to top of grid
		const grid = CollectionDOM.grid;
		if (grid) {
			grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}
};

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS (for backward compatibility)
 */
window.mmSortByChanged = (select) => {
	if (!select) return;
	
	// Delegate to the central SortBy handler for sync and AJAX
	SortBy.handleChange({ target: select });
};

// Mobile filter functions
window.mmOpenFiltersMobile = () => MobileFilters.open();
window.mmCloseFiltersMobile = () => MobileFilters.close();
window.mmCheckDrawer = () => MobileFilters.checkDrawer();

/*********************************************************************************************
 * 📌 STICKY FILTER CTA (MOBILE)
 */
const StickyFilterCTA = {
	initialized: false,

	init() {
		this.wrapper = document.querySelector('[data-sticky-filter-cta]');
		this.headerMobile = document.querySelector('.mm-collection-header');
		this.productGrid = document.querySelector('.mm-collection-grid-products-grid');

		if (!this.wrapper || !this.headerMobile || !this.productGrid) return;

		if (this.initialized) {
			this.refreshObserver();
			return;
		}

		this.setupObserver();
		this.initialized = true;
	},

	_evaluate() {
		if (!this.wrapper || !this.headerMobile || !this.productGrid) return;

		const headerRect = this.headerMobile.getBoundingClientRect();
		const gridRect = this.productGrid.getBoundingClientRect();

		const headerIsAbove = headerRect.bottom < 0;
		const gridVisible = gridRect.bottom > 100 && gridRect.top < window.innerHeight;

		if (headerIsAbove && gridVisible) {
			this.wrapper.classList.add('is-visible');
		} else {
			this.wrapper.classList.remove('is-visible');
		}
	},

	setupObserver() {
		const options = { root: null, threshold: 0 };

		this.observer = new IntersectionObserver(() => this._evaluate(), options);
		this.observer.observe(this.headerMobile);
		this.observer.observe(this.productGrid);

		window.addEventListener('scroll', collectionDebounce(() => this._evaluate(), 10), { passive: true });

		this._evaluate();
	},

	refreshObserver() {
		if (this.observer) {
			this.observer.disconnect();
			if (this.headerMobile) this.observer.observe(this.headerMobile);
			if (this.productGrid) this.observer.observe(this.productGrid);
		}
		// Réévaluation après reflow AJAX (le DOM peut avoir changé de hauteur)
		requestAnimationFrame(() => this._evaluate());
	}
};

/*********************************************************************************************
 * 🔘 DESKTOP FILTER PILLS
 */
const DesktopFilterPills = {
	_activeTrigger: null,

	closePills(restoreFocus = false) {
		document.querySelectorAll('[data-desktop-pill].is-active').forEach(p => {
			p.classList.remove('is-active');
			p.querySelector('[data-desktop-pill-trigger]')?.setAttribute('aria-expanded', 'false');
		});
		if (restoreFocus && this._activeTrigger) {
			this._activeTrigger.focus();
		}
		this._activeTrigger = null;
	},

	init() {
		if (this.initialized) return;

		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('[data-desktop-pill-trigger]');
			if (trigger) {
				const pill = trigger.closest('[data-desktop-pill]');
				const isActive = pill.classList.contains('is-active');
				this.closePills();
				if (!isActive) {
					pill.classList.add('is-active');
					trigger.setAttribute('aria-expanded', 'true');
					this._activeTrigger = trigger;
					requestAnimationFrame(() => {
						const firstOption = pill.querySelector('.mm-filter-pill-option[tabindex="0"]');
						if (firstOption) firstOption.focus();
					});
				} else {
					trigger.focus();
				}
				e.stopPropagation();
				return;
			}
			if (!e.target.closest('[data-desktop-pill]')) this.closePills();
		});

		document.addEventListener('keydown', (e) => {
			// Escape : ferme le pill et rend le focus au trigger
			if (e.key === 'Escape') {
				if (document.querySelector('[data-desktop-pill].is-active')) { this.closePills(true); return; }
			}

			// Tab : focus trap à l'intérieur du dropdown ouvert
			if (e.key === 'Tab') {
				const activePill = document.querySelector('[data-desktop-pill].is-active');
				if (!activePill) return;
				const dropdown = activePill.querySelector('[data-desktop-pill-dropdown]');
				if (!dropdown?.contains(document.activeElement)) return;
				const options = Array.from(dropdown.querySelectorAll('.mm-filter-pill-option[tabindex="0"]'));
				if (options.length === 0) return;
				e.preventDefault();
				const idx = options.indexOf(document.activeElement);
				const next = e.shiftKey
					? options[(idx - 1 + options.length) % options.length]
					: options[(idx + 1) % options.length];
				next.focus();
				return;
			}

			// Entrée/Espace : coche/décoche la case de l'option focusée
			if (e.key === 'Enter' || e.key === ' ') {
				const opt = document.activeElement;
				if (!opt?.classList.contains('mm-filter-pill-option')) return;
				e.preventDefault();
				const cb = opt.querySelector('input[type="checkbox"]');
				if (cb && !cb.disabled) {
					cb.checked = !cb.checked;
					cb.dispatchEvent(new Event('change', { bubbles: true }));
				}
			}
		});

		// Avant AJAX : sauvegarder l'index du pill ouvert
		document.addEventListener('collection:filter-start', () => {
			const pills = [...document.querySelectorAll('[data-desktop-pill]')];
			const active = pills.find(p => p.classList.contains('is-active'));
			this._activePillIndex = active ? pills.indexOf(active) : -1;
		});

		// Après AJAX : restaurer le pill sans animation
		document.addEventListener('collection:filter-success', () => {
			if (this._activePillIndex >= 0) {
				const pill = document.querySelectorAll('[data-desktop-pill]')[this._activePillIndex];
				if (pill) {
					const dropdown = pill.querySelector('[data-desktop-pill-dropdown]');
					if (dropdown) dropdown.style.transition = 'none';
					pill.classList.add('is-active');
					pill.querySelector('[data-desktop-pill-trigger]')?.setAttribute('aria-expanded', 'true');
					if (dropdown) { dropdown.offsetHeight; dropdown.style.transition = ''; }
				}
				this._activePillIndex = -1;
			}
		});

		this.initialized = true;
	}
};

/*********************************************************************************************
 * 📱 MOBILE FILTER SYNC (mobile checkboxes → desktop form)
 */
const MobileFilterSync = {
	init() {
		if (this.initialized) return;

		document.addEventListener('change', (e) => {
			const mobileInput = e.target.closest('[data-mobile-filter-input]');
			if (!mobileInput) return;

			const desktopInput = document.querySelector(
				`[data-desktop-filter-form] input[name="${mobileInput.name}"][value="${mobileInput.value}"]`
			);
			if (desktopInput) {
				desktopInput.checked = mobileInput.checked;
				CollectionFilters.submitForm();
			}
		});

		this.initialized = true;
	}
};

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	collectionEmit('init-start');

	try {
		CollectionFilters.init();
		FilterAccordions.init(null, true);
		PriceSliders.init();
		SortBy.init();
		Pagination.init();
		MobileFilters.init();
		MobileFilters.checkDrawer();
		StickyFilterCTA.init();
		DesktopFilterPills.init();
		MobileFilterSync.init();
		
		collectionEmit('init-success');
	} catch (error) {
		collectionEmit('init-error', { error: error.message });
		collectionLog('error', 'Collection initialization failed', { error: error.message });
	}
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	// Filter close button
	const filterCloseBtn = document.querySelector('[data-filter-close]');
	if (filterCloseBtn) {
		filterCloseBtn.addEventListener('click', () => {
			if (typeof window.mmCloseFiltersMobile === 'function') {
				window.mmCloseFiltersMobile();
			}
		});
	}

	// Filter submit button
	const filterSubmitBtn = document.querySelector('[data-filter-submit]');
	if (filterSubmitBtn) {
		filterSubmitBtn.addEventListener('click', () => {
			if (typeof window.fakeSubmitForm === 'function') {
				window.fakeSubmitForm();
			}
		});
	}

	// Open filters mobile buttons are now handled by MobileFilters.init()
});

/*********************************************************************************************
 * 🎨 THEME EDITOR SUPPORT
 * Ensures the UI remains interactive and updated when settings change in the Shopify Editor
 */
if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
        const sectionId = event.detail.sectionId;
        const sectionElement = event.target;

        // Check if this is the collection grid section
        if (sectionElement.classList.contains('mm-section-collection-grid') || 
            sectionElement.querySelector('[data-collection-grid]')) {
            
            collectionLog('info', 'Re-initializing for Theme Editor', { sectionId });
            
            try {
                CollectionFilters.init();
                FilterAccordions.init(null, true);
                PriceSliders.init();
                SortBy.init();
                Pagination.init();
                MobileFilters.init();
                StickyFilterCTA.init();
                
                // Also re-init global animations if available
                if (typeof window.initButtonCharacterStagger === 'function') window.initButtonCharacterStagger();
                if (typeof AnimationManager !== 'undefined') {
                    AnimationManager.initialized = false;
                    AnimationManager.init();
                }
            } catch (error) {
                collectionLog('error', 'Theme Editor re-initialization failed', { error: error.message });
            }
        }
    });

    // Re-init on block select/deselect if needed for specific focus
    document.addEventListener('shopify:block:select', (event) => {
        if (event.target.classList.contains('mm-breaking-block')) {
            if (typeof window.initButtonCharacterStagger === 'function') window.initButtonCharacterStagger();
        }
    });
}

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (COLLECTION_CONFIG.isDevelopment) {
	['init-success', 'filter-success', 'input-change', 'form-submit-start', 'price-update', 'sort-change'].forEach(event => {
		document.addEventListener(`collection:${event}`, e =>
			console.log(`🏷️ [COLLECTION] ${event}:`, e.detail)
		);
	});
}